import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { messagesFor } from './catalogues';
import { en } from './en';
import { isLocale, resolveLocale, type Locale } from './locale';
import type { Messages } from './messages';

/** What the React tree reads (ADR-031): the effective locale, its catalogue, and the switch. */
export interface I18n {
  readonly locale: Locale;
  readonly messages: Messages;
  /** Makes `locale` the user's explicit choice and persists it. */
  setLocale(locale: Locale): void;
}

/** Where this install keeps the user's language choice — the platform port's two methods. */
export interface LanguageStore {
  /** The stored choice, or null when the user never made one (anything else is ignored). */
  load(): Promise<string | null>;
  save(locale: Locale): Promise<void>;
}

/**
 * The default is English with a no-op switch, so a component rendered without a provider —
 * every component test that does not care about language — renders English unchanged.
 * Exported for the tests that pin a locale synchronously; the app mounts `LocaleProvider`.
 */
export const I18nContext = createContext<I18n>({
  locale: 'en',
  messages: en,
  setLocale: () => undefined,
});

/**
 * Resolves the locale the UI renders in (ADR-031) — the user's stored choice when it names a
 * supported locale, else the system's (`systemLanguages()`, re-read on `languagechange`, which
 * is how an Android per-app language change reaches the WebView), else English — and renders
 * the children only once the stored choice has been read, so there is never a flash of the
 * wrong language. Keeps `<html lang>` in step with the effective locale.
 */
export function LocaleProvider({
  systemLanguages,
  store,
  children,
}: {
  /** The host's preference-ordered language tags (`navigator.languages`). */
  systemLanguages: () => readonly string[];
  store: LanguageStore;
  children: ReactNode;
}) {
  const [system, setSystem] = useState<Locale>(() => resolveLocale(systemLanguages()));
  const [choice, setChoice] = useState<Locale | null>(null);
  const [ready, setReady] = useState(false);

  // The latest props, read by the one-time effects and the stable switch below.
  const latest = useRef({ systemLanguages, store });
  latest.current = { systemLanguages, store };

  // The stored choice, once: a valid locale is adopted; null, garbage or a failing store is
  // not. Either way the children render only after the answer is in.
  useEffect(() => {
    let cancelled = false;
    latest.current.store
      .load()
      .then((stored) => {
        if (!cancelled && isLocale(stored)) setChoice(stored);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The system locale follows the host; it only shows through while there is no choice.
  useEffect(() => {
    const changed = () => setSystem(resolveLocale(latest.current.systemLanguages()));
    window.addEventListener('languagechange', changed);
    return () => window.removeEventListener('languagechange', changed);
  }, []);

  const locale = choice ?? system;

  // A layout effect, so `<html lang>` changes in the same commit as the text it describes.
  useLayoutEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setChoice(next);
    // A store that cannot write (a privacy mode) costs the user nothing but persistence.
    void latest.current.store.save(next).catch(() => undefined);
  }, []);

  const value = useMemo<I18n>(
    () => ({ locale, messages: messagesFor(locale), setLocale }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{ready ? children : null}</I18nContext.Provider>;
}

/** The effective locale, its catalogue and the switch. */
export function useI18n(): I18n {
  return useContext(I18nContext);
}

/** The catalogue to render with — what nearly every component needs. */
export function useMessages(): Messages {
  return useContext(I18nContext).messages;
}
